/**
 * OracleGuidancePanel.tsx - Actionable Guidance Panel
 *
 * Answers the user's third question: "How do I use it?"
 * Provides confidence-based guidance and suggested next actions
 * with cross-tab navigation CTAs.
 *
 * Per wobbly-nibbling-chipmunk.md Phase 3
 * Per REASONING_ORACLE_PROTOTYPE_SELECTION_v3.md Section 12.3
 */

import { useMemo } from 'react'
import type { ConfidenceResult, InferenceChain } from '@models/oracle'
import type { CenterTab } from '@contexts/TabNavigationContext'
import './OracleGuidancePanel.css'

interface OracleGuidancePanelProps {
  confidence: ConfidenceResult
  inferenceChains: InferenceChain[]
  gaps: string[]
  onNavigateToTab: (tab: CenterTab) => void
  onHighlightInGraph?: (nodeId: string) => void
}

// Confidence level messaging
const CONFIDENCE_MESSAGES: Record<string, { icon: string; title: string; message: string }> = {
  HIGH: {
    icon: '✅',
    title: 'HIGH CONFIDENCE',
    message:
      'Your recommendations are strongly grounded in your company\'s business relationships. You can proceed with confidence.',
  },
  MEDIUM: {
    icon: '⚠️',
    title: 'MEDIUM CONFIDENCE',
    message:
      'Good evidence exists to support these recommendations, but some areas may benefit from additional review or validation.',
  },
  LOW: {
    icon: '⚡',
    title: 'LOW CONFIDENCE',
    message:
      'Limited evidence supports these recommendations. Consider exploring alternative approaches or gathering more data.',
  },
  INSUFFICIENT: {
    icon: '❌',
    title: 'INSUFFICIENT DATA',
    message:
      'Not enough evidence to confidently support these recommendations. Review the identified gaps before proceeding.',
  },
}

// Get the strongest node from inference chains (most frequently matched)
function getStrongestNode(chains: InferenceChain[]): string | null {
  const nodeCount = new Map<string, number>()

  chains.forEach((chain) => {
    chain.matched_nodes.forEach((nodeId) => {
      nodeCount.set(nodeId, (nodeCount.get(nodeId) || 0) + 1)
    })
  })

  let maxNode: string | null = null
  let maxCount = 0

  nodeCount.forEach((count, nodeId) => {
    if (count > maxCount) {
      maxCount = count
      maxNode = nodeId
    }
  })

  return maxNode
}

// Format node ID for display
function formatNodeId(nodeId: string): string {
  return nodeId.replace(/_id$/, '').replace(/_/g, ' ')
}

function OracleGuidancePanel({
  confidence,
  inferenceChains,
  gaps,
  onNavigateToTab,
  onHighlightInGraph,
}: OracleGuidancePanelProps) {
  const confidencePercent = Math.round(confidence.score * 100)
  const confidenceLevel = confidence.level
  const confidenceInfo = CONFIDENCE_MESSAGES[confidenceLevel] || CONFIDENCE_MESSAGES.MEDIUM

  // Find strongest evidence node for graph exploration suggestion
  const strongestNode = useMemo(() => getStrongestNode(inferenceChains), [inferenceChains])

  // Count total validated concepts
  const totalValidated = useMemo(() => {
    const allNodes = new Set(inferenceChains.flatMap((c) => c.matched_nodes))
    return allNodes.size
  }, [inferenceChains])

  // Determine suggested actions based on confidence and gaps
  const suggestedActions = useMemo(() => {
    const actions: Array<{
      id: string
      icon: string
      title: string
      description: string
      action: () => void
      actionLabel: string
      priority: 'high' | 'medium' | 'low'
    }> = []

    // Action 1: Review MECE Tactics (always suggested)
    actions.push({
      id: 'mece',
      icon: '📋',
      title: 'Review MECE Tactics',
      description:
        confidenceLevel === 'HIGH' || confidenceLevel === 'MEDIUM'
          ? `${totalValidated} validated concepts support your strategic pillars.`
          : 'Review the recommended tactics and consider alternatives.',
      action: () => onNavigateToTab('workspace'),
      actionLabel: 'Go to Strategy Workspace',
      priority: 'high',
    })

    // Action 2: Explore the Graph (if we have a strong node)
    if (strongestNode) {
      actions.push({
        id: 'graph',
        icon: '🔗',
        title: 'Explore the Graph',
        description: `See how ${formatNodeId(strongestNode)} connects to other business concepts.`,
        action: () => {
          if (onHighlightInGraph) {
            onHighlightInGraph(strongestNode)
          }
          onNavigateToTab('graph')
        },
        actionLabel: 'Highlight in Business Graph',
        priority: 'medium',
      })
    }

    // Action 3: View Analysis Report
    actions.push({
      id: 'report',
      icon: '📄',
      title: 'View Full Analysis',
      description: 'Read the complete strategic analysis with methodology details.',
      action: () => onNavigateToTab('report'),
      actionLabel: 'Go to Analysis Report',
      priority: 'medium',
    })

    // Action 4: Investigate Gaps (if gaps exist)
    if (gaps.length > 0) {
      actions.push({
        id: 'gaps',
        icon: '🔍',
        title: 'Investigate Gaps',
        description: `${gaps.length} concept${gaps.length !== 1 ? 's' : ''} not found in your ontology. Consider adding these for better coverage.`,
        action: () => {}, // Gap investigation is informational
        actionLabel: '',
        priority: confidenceLevel === 'LOW' || confidenceLevel === 'INSUFFICIENT' ? 'high' : 'low',
      })
    } else {
      actions.push({
        id: 'no-gaps',
        icon: '✓',
        title: 'Complete Coverage',
        description: 'All inferred concepts exist in your ontology. No gaps identified.',
        action: () => {},
        actionLabel: '',
        priority: 'low',
      })
    }

    return actions
  }, [
    confidenceLevel,
    totalValidated,
    strongestNode,
    gaps,
    onNavigateToTab,
    onHighlightInGraph,
  ])

  return (
    <div className="oracle-guidance-panel">
      {/* Header */}
      <div className="guidance-header">
        <span className="guidance-icon">📋</span>
        <h3>What To Do With These Insights</h3>
      </div>

      {/* Confidence Summary */}
      <div className={`confidence-summary ${confidenceLevel.toLowerCase()}`}>
        <div className="confidence-badge">
          <span className="badge-icon">{confidenceInfo.icon}</span>
          <span className="badge-label">{confidenceInfo.title}</span>
          <span className="badge-percent">({confidencePercent}%)</span>
        </div>
        <p className="confidence-message">{confidenceInfo.message}</p>
      </div>

      {/* Suggested Actions */}
      <div className="suggested-actions">
        <h4>Suggested Actions</h4>
        <div className="actions-list">
          {suggestedActions.map((action, idx) => (
            <div
              key={action.id}
              className={`action-item ${action.priority}`}
            >
              <div className="action-number">{idx + 1}</div>
              <div className="action-content">
                <div className="action-header">
                  <span className="action-icon">{action.icon}</span>
                  <span className="action-title">{action.title}</span>
                </div>
                <p className="action-description">{action.description}</p>
                {action.actionLabel && (
                  <button
                    className="action-button"
                    onClick={action.action}
                  >
                    {action.actionLabel} →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="guidance-stats">
        <div className="stat">
          <span className="stat-value">{inferenceChains.length}</span>
          <span className="stat-label">Causal Chains</span>
        </div>
        <div className="stat">
          <span className="stat-value">{totalValidated}</span>
          <span className="stat-label">Validated Concepts</span>
        </div>
        <div className="stat">
          <span className="stat-value">{gaps.length}</span>
          <span className="stat-label">Gaps Identified</span>
        </div>
      </div>
    </div>
  )
}

export default OracleGuidancePanel
